import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Box, List, ListItem,
  ListItemIcon, Badge, Chip, Button,
  Collapse, IconButton, CircularProgress, Popover
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import { NotificationItem } from '../Types/Storage';
import UserContext from '../User/UserSettings/UserContext';

interface NotificationWidgetProps {
  notifications: NotificationItem[];
  onDismissNotification: (notificationId: string) => void;
  onStorageSelect?: (storageId: string) => void;
  loading?: boolean;
}

const NotificationWidget: React.FC<NotificationWidgetProps> = ({
  notifications,
  onDismissNotification,
  onStorageSelect,
  loading = false
}) => {
  const [expanded, setExpanded] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedAlarms, setSelectedAlarms] = useState<string[]>([]);
  const { user } = useContext(UserContext) || {};
  const navigate = useNavigate();

  const handleAlarmHover = (event: React.MouseEvent<HTMLElement>, alarms: string[]) => {
    if (alarms.length > 1) {
      setAnchorEl(event.currentTarget);
      setSelectedAlarms(alarms);
    }
  };

  const handleAlarmLeave = () => {
    setAnchorEl(null);
    setSelectedAlarms([]);
  };

  const openPopover = Boolean(anchorEl);

  const handleNotificationClick = (notification: NotificationItem) => {
    // If it's a lab notification with navigation data, navigate to reports page
    if (notification.type === 'lab' && notification.locationId && notification.binName && notification.cropYear) {
      const params = new URLSearchParams({
        locationId: notification.locationId.toString(),
        binName: notification.binName,
        cropYear: notification.cropYear.toString()
      });
      navigate(`/reports?${params.toString()}`);
    }
    // If it's an alarm notification, select the storage
    else if (notification.type === 'alarm' && notification.storageId && onStorageSelect) {
      onStorageSelect(notification.storageId);
    }
  };

  const handleDismissNotification = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation(); // Prevent navigation when dismissing
    onDismissNotification(notificationId);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'lab': return <ScienceIcon color="primary" />;
      case 'alarm': return <WarningIcon color="error" />;
      default: return <NotificationsIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours === 1) return '1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return '1 day ago';
    return `${diffInDays} days ago`;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ pb: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Badge badgeContent={notifications.length} color="error">
              <NotificationsIcon color="primary" />
            </Badge>
            <Typography variant="h6" fontWeight="bold">
              Notifications
            </Typography>
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box textAlign="center" py={3}>
              <Typography variant="body2" color="text.secondary">
                No notifications at this time
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <List
                sx={{
                  p: 0,
                  flex: 1,
                  maxHeight: '320px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    },
                  },
                }}
              >
                {notifications.map((notification) => {
                  // Compact rendering for alarm notifications
                  if (notification.type === 'alarm') {
                    const firstAlarm = notification.alarms?.[0] || 'Active alarm';
                    const alarmCount = notification.alarms?.length || 0;

                    return (
                      <ListItem
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        onMouseEnter={(e) => {
                          if (notification.alarms) {
                            handleAlarmHover(e, notification.alarms);
                          }
                        }}
                        onMouseLeave={handleAlarmLeave}
                        sx={{
                          px: 1,
                          py: 0.75,
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: 'rgba(211, 47, 47, 0.08)',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'rgba(211, 47, 47, 0.15)'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <WarningIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ flex: 1 }}
                              noWrap
                            >
                              {notification.storageName}
                            </Typography>
                            {alarmCount > 1 && (
                              <Chip
                                label={`${alarmCount}`}
                                size="small"
                                color="error"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {firstAlarm}
                          </Typography>
                        </Box>
                      </ListItem>
                    );
                  }

                  // Standard rendering for other notification types
                  return (
                    <ListItem
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        px: 0,
                        py: 1,
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'action.hover',
                        cursor: notification.type === 'lab' ? 'pointer' : 'default',
                        '&:hover': {
                          bgcolor: 'action.selected'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getIcon(notification.type)}
                      </ListItemIcon>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={'bold'}
                            sx={{ flex: 1 }}
                          >
                            {notification.title}
                          </Typography>
                          <Chip
                            label={notification.priority}
                            size="small"
                            color={getPriorityColor(notification.priority) as any}
                            variant="outlined"
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {notification.message}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(notification.timestamp)}
                          </Typography>
                          {notification.dismissable !== false && (
                            <Button
                              size="small"
                              onClick={(e) => handleDismissNotification(e, notification.id)}
                              sx={{ minWidth: 'auto', p: 0.5 }}
                              title="Dismiss notification"
                            >
                              <CloseIcon fontSize="small" />
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Collapse>
      </CardContent>

      {/* Popover for showing all alarms */}
      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleAlarmLeave}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        hideBackdrop
      >
        <Box sx={{ p: 2, maxWidth: 350 }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1} color="error.main">
            All Active Alarms ({selectedAlarms.length})
          </Typography>
          <List dense sx={{ p: 0 }}>
            {selectedAlarms.map((alarm, index) => (
              <ListItem key={index} sx={{ px: 0, py: 0.75, alignItems: 'flex-start' }}>
                <Box component="span" sx={{ mr: 1, mt: 0.25, color: 'error.main', fontWeight: 'bold' }}>
                  •
                </Box>
                <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.5 }}>
                  {alarm}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Card>
  );
};

export default NotificationWidget;
